/**
 * PriceContextBar - Compact Price/Session/ATR Summary Header
 *
 * Single-row context bar showing key trading context.
 * Symbol selector dropdown, price, trend, session, ATR in minimal space.
 */

import React, { memo, useState } from 'react';
import { Box, Typography, IconButton, Tooltip, Menu, MenuItem } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

/**
 * Get trend color
 */
const getTrendColor = (trend) => {
    if (!trend) return '#6b7280';
    const t = trend.toUpperCase();
    if (t.includes('STRONG') && t.includes('UP')) return '#22c55e';
    if (t.includes('UP')) return '#4ade80';
    if (t.includes('STRONG') && t.includes('DOWN')) return '#ef4444';
    if (t.includes('DOWN')) return '#f87171';
    return '#fbbf24';
};

/**
 * Get trend arrow
 */
const getTrendArrow = (trend) => {
    if (!trend) return '→';
    const t = trend.toUpperCase();
    if (t.includes('UP')) return '▲';
    if (t.includes('DOWN')) return '▼';
    return '→';
};

/**
 * Get session color
 */
const getSessionColor = (session) => {
    if (!session) return '#6b7280';
    const s = session.toUpperCase();
    if (s === 'LONDON' || s === 'EUROPE') return '#3b82f6';
    if (s === 'US' || s === 'AMERICA' || s === 'NEW_YORK') return '#22c55e';
    if (s === 'ASIA' || s === 'TOKYO' || s === 'SYDNEY') return '#a855f7';
    if (s === 'CLOSED') return '#ef4444';
    return '#fbbf24';
};

/**
 * Default available symbols for trading
 */
const DEFAULT_SYMBOLS = [
    'XAUUSD_ct',
    'BTCUSD_ct',
    'EURUSD_ct',
    'GBPUSD_ct',
    'XAGUSD_ct',
    'ETHUSD_ct'
];

/**
 * Symbols with full QuantX historical data (100+ H4 bars for EMA calculations)
 * Other symbols have Strategy DNA events but are still accumulating OHLC bars
 */
const SYMBOLS_WITH_QUANTX_DATA = ['XAUUSD_ct'];

/**
 * PriceContextBar Component
 */
const PriceContextBar = memo(({
    symbol,
    currentPrice,
    overallTrend,
    trendScore,
    session,
    atr,
    atrPercent,
    signalRConnected = false,
    onRefresh,
    isLoading = false,
    availableSymbols = DEFAULT_SYMBOLS,
    onSymbolChange
}) => {
    const [symbolMenuAnchor, setSymbolMenuAnchor] = useState(null);

    const handleSymbolClick = (event) => {
        if (onSymbolChange && availableSymbols.length > 0) {
            setSymbolMenuAnchor(event.currentTarget);
        }
    };

    const handleSymbolSelect = (newSymbol) => {
        if (onSymbolChange) {
            onSymbolChange(newSymbol);
        }
        setSymbolMenuAnchor(null);
    };

    const handleSymbolClose = () => {
        setSymbolMenuAnchor(null);
    };

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                backgroundColor: '#1a1a1a',
                borderRadius: '3px',
                padding: '5px 10px',
                border: '1px solid #374151',
                gap: 1,
                flexWrap: 'wrap'
            }}
        >
            {/* Left: Symbol + Price */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {/* Connection Status */}
                <Box
                    sx={{
                        width: '6px',
                        height: '6px',
                        borderRadius: '50%',
                        backgroundColor: signalRConnected ? '#22c55e' : '#ef4444',
                        boxShadow: signalRConnected ? '0 0 4px #22c55e' : 'none'
                    }}
                />

                {/* Symbol Selector */}
                <Box
                    onClick={handleSymbolClick}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.3,
                        cursor: onSymbolChange ? 'pointer' : 'default',
                        padding: '2px 4px',
                        borderRadius: '2px',
                        '&:hover': onSymbolChange ? {
                            backgroundColor: 'rgba(55, 65, 81, 0.5)'
                        } : {}
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: '11px',
                            fontWeight: 700,
                            color: '#ef4444',
                            fontFamily: 'monospace',
                            letterSpacing: '0.5px'
                        }}
                    >
                        {symbol || 'SYMBOL'}
                    </Typography>
                    {onSymbolChange && (
                        <KeyboardArrowDownIcon sx={{ fontSize: 14, color: '#6b7280' }} />
                    )}
                </Box>

                {/* Symbol Dropdown Menu */}
                <Menu
                    anchorEl={symbolMenuAnchor}
                    open={Boolean(symbolMenuAnchor)}
                    onClose={handleSymbolClose}
                    slotProps={{
                        paper: {
                            sx: {
                                backgroundColor: '#1a1a1a',
                                border: '1px solid #374151',
                                minWidth: '140px'
                            }
                        }
                    }}
                >
                    {availableSymbols.map((sym) => {
                        const hasFullData = SYMBOLS_WITH_QUANTX_DATA.includes(sym);
                        return (
                            <MenuItem
                                key={sym}
                                onClick={() => handleSymbolSelect(sym)}
                                selected={sym === symbol}
                                sx={{
                                    fontSize: '11px',
                                    fontFamily: 'monospace',
                                    color: sym === symbol ? '#ef4444' : '#fff',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    gap: 1,
                                    '&:hover': {
                                        backgroundColor: '#262626'
                                    },
                                    '&.Mui-selected': {
                                        backgroundColor: 'rgba(220, 38, 38, 0.2)',
                                        '&:hover': {
                                            backgroundColor: 'rgba(220, 38, 38, 0.3)'
                                        }
                                    }
                                }}
                            >
                                <span>{sym}</span>
                                {hasFullData ? (
                                    <span style={{ fontSize: '8px', color: '#22c55e' }}>READY</span>
                                ) : (
                                    <span style={{ fontSize: '8px', color: '#fbbf24' }}>LOADING</span>
                                )}
                            </MenuItem>
                        );
                    })}
                </Menu>

                {/* Price */}
                <Typography
                    sx={{
                        fontSize: '12px',
                        fontWeight: 700,
                        color: '#fff',
                        fontFamily: 'monospace'
                    }}
                >
                    {currentPrice?.toFixed(5) || '--'}
                </Typography>
            </Box>

            {/* Center: Trend */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Typography
                    sx={{
                        fontSize: '10px',
                        fontWeight: 700,
                        color: getTrendColor(overallTrend),
                        fontFamily: 'monospace'
                    }}
                >
                    {getTrendArrow(overallTrend)} {overallTrend?.replace('_', ' ') || '--'}
                </Typography>
                {trendScore !== undefined && (
                    <Box
                        sx={{
                            backgroundColor: getTrendColor(overallTrend),
                            color: '#fff',
                            padding: '1px 6px',
                            borderRadius: '2px'
                        }}
                    >
                        <Typography sx={{ fontSize: '9px', fontWeight: 700, fontFamily: 'monospace' }}>
                            {trendScore}/100
                        </Typography>
                    </Box>
                )}
            </Box>

            {/* Right: Session + ATR */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {/* Session */}
                {session && (
                    <Box
                        sx={{
                            backgroundColor: 'rgba(55, 65, 81, 0.5)',
                            padding: '1px 6px',
                            borderRadius: '2px'
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: '9px',
                                fontWeight: 700,
                                color: getSessionColor(session),
                                fontFamily: 'monospace'
                            }}
                        >
                            {session}
                        </Typography>
                    </Box>
                )}

                {/* ATR */}
                {atr && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography sx={{ fontSize: '9px', color: '#6b7280', fontFamily: 'monospace' }}>
                            ATR:
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: '9px',
                                fontWeight: 600,
                                color: atrPercent > 1 ? '#fbbf24' : '#9ca3af',
                                fontFamily: 'monospace'
                            }}
                        >
                            {atr.toFixed(4)} ({atrPercent?.toFixed(2) || '--'}%)
                        </Typography>
                    </Box>
                )}

                {/* Refresh Button */}
                {onRefresh && (
                    <Tooltip title="Refresh Data" arrow placement="top">
                        <IconButton
                            onClick={onRefresh}
                            disabled={isLoading}
                            size="small"
                            sx={{
                                padding: '2px',
                                color: isLoading ? '#6b7280' : '#9ca3af',
                                '&:hover': { color: '#fff', backgroundColor: 'rgba(55, 65, 81, 0.5)' },
                                animation: isLoading ? 'spin 1s linear infinite' : 'none',
                                '@keyframes spin': {
                                    '0%': { transform: 'rotate(0deg)' },
                                    '100%': { transform: 'rotate(360deg)' }
                                }
                            }}
                        >
                            <RefreshIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                    </Tooltip>
                )}
            </Box>
        </Box>
    );
});

PriceContextBar.displayName = 'PriceContextBar';

export default PriceContextBar;
